import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  Phone, 
  Mail, 
  MessageSquare,
  Users,
  Zap,
  Crown
} from 'lucide-react';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    services: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.projectType) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Simulate form submission
    toast.success('Consultation request submitted! We\'ll contact you within 24 hours.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      website: '',
      projectType: '',
      budget: '',
      timeline: '',
      description: '',
      services: []
    });
  };

  const handleServiceChange = (service: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, service]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        services: prev.services.filter(s => s !== service)
      }));
    }
  };

  const consultationBenefits = [
    "Free website audit and analysis",
    "Custom strategy recommendations", 
    "Transparent pricing discussion",
    "Timeline and milestone planning",
    "Technology stack recommendations",
    "No obligation or pressure"
  ];

  const serviceOptions = [
    "Website Design & Development",
    "Website Redesign",
    "E-commerce Development", 
    "SEO Optimization",
    "Website Maintenance",
    "Client Training",
    "Performance Optimization",
    "Content Management"
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Free Consultation
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Let's Build Your
            <span className="text-gradient"> Digital Success</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Schedule a free consultation to discuss your project goals, timeline, and how 
            we can help you take complete control of your online presence.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
            
            {/* Consultation Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-elegant">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl font-bold text-foreground">
                    Schedule Your Free Consultation
                  </CardTitle>
                  <p className="font-body text-muted-foreground">
                    Tell us about your project and we'll provide a custom strategy and transparent pricing.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="font-body font-semibold">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-body font-semibold">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="font-body font-semibold">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="font-body font-semibold">
                          Company Name
                        </Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                          placeholder="Your Company"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="font-body font-semibold">
                        Current Website (if any)
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>

                    {/* Project Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="projectType" className="font-body font-semibold">
                          Project Type *
                        </Label>
                        <Select 
                          value={formData.projectType} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, projectType: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select project type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="foundation">Foundation Website</SelectItem>
                            <SelectItem value="custom">Custom Development</SelectItem>
                            <SelectItem value="redesign">Website Redesign</SelectItem>
                            <SelectItem value="ecommerce">E-commerce Site</SelectItem>
                            <SelectItem value="maintenance">Website Maintenance</SelectItem>
                            <SelectItem value="consultation">Just Consultation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget" className="font-body font-semibold">
                          Budget Range
                        </Label>
                        <Select 
                          value={formData.budget} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, budget: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select budget range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-3k">Under $3,000</SelectItem>
                            <SelectItem value="3k-5k">$3,000 - $5,000</SelectItem>
                            <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                            <SelectItem value="10k-plus">$10,000+</SelectItem>
                            <SelectItem value="discuss">Let's Discuss</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timeline" className="font-body font-semibold">
                        Desired Timeline
                      </Label>
                      <Select 
                        value={formData.timeline} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, timeline: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="When do you need this completed?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asap">ASAP</SelectItem>
                          <SelectItem value="1-month">Within 1 month</SelectItem>
                          <SelectItem value="2-3-months">2-3 months</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Services Needed */}
                    <div className="space-y-3">
                      <Label className="font-body font-semibold">
                        Services Needed (select all that apply)
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {serviceOptions.map((service) => (
                          <div key={service} className="flex items-center space-x-2">
                            <Checkbox
                              id={service}
                              checked={formData.services.includes(service)}
                              onCheckedChange={(checked) => handleServiceChange(service, checked as boolean)}
                            />
                            <Label htmlFor={service} className="font-body text-sm">
                              {service}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Project Description */}
                    <div className="space-y-2">
                      <Label htmlFor="description" className="font-body font-semibold">
                        Project Description
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Tell us about your project goals, target audience, and any specific requirements..."
                        rows={4}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                    >
                      Schedule Free Consultation
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* What to Expect */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg font-bold text-foreground flex items-center">
                    <CheckCircle className="h-5 w-5 text-accent mr-2" />
                    What to Expect
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {consultationBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{benefit}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg font-bold text-foreground">
                    Prefer to Call?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm text-foreground">(555) 123-4567</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm text-foreground">hello@successpro.digital</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-4 w-4 text-accent" />
                    <span className="font-body text-sm text-foreground">Mon-Fri, 9AM-6PM EST</span>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Packages */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg font-bold text-foreground">
                    Quick Package Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Zap className="h-4 w-4 text-primary" />
                      <span className="font-body text-sm font-semibold">Foundation</span>
                    </div>
                    <span className="font-heading text-sm font-bold text-primary">$2,997</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Crown className="h-4 w-4 text-accent" />
                      <span className="font-body text-sm font-semibold">Custom</span>
                    </div>
                    <span className="font-heading text-sm font-bold text-accent">$7,997+</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <Card className="shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    How long does a typical project take?
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Foundation websites typically take 3-4 weeks, while custom projects 
                    range from 4-8 weeks depending on complexity.
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    Do you provide training?
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Yes! Every project includes comprehensive training so you can 
                    manage your website independently.
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    What's included in the consultation?
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    We'll audit your current site, discuss your goals, and provide 
                    a custom strategy with transparent pricing.
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    Do you offer payment plans?
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Yes, we offer flexible payment plans to make our services 
                    accessible to businesses of all sizes.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;